/**
 * Builds full application config from minimal config + hostname.
 * This keeps URL construction logic in compiled JS rather than plain HTML.
 */

const SERVICES = ['auth', 'collaboration', 'data', 'event', 'file', 'model', 'site', 'integration'];

/**
 * Build full config from minimal config + browser hostname.
 * @param {Object} minimalConfig - Minimal runtime configuration. Expected keys include
 *   `auth.url` (base auth server URL) and/or `services.domain` (base domain used to
 *   construct per-service endpoints). May also include local overrides (e.g. per-service
 *   `endpoint` or `auth.realm`) that come from `local.json`.
 * @param {string} [hostname] - Optional hostname override (defaults to window.location.hostname)
 * @returns {Object} Full application config
 */
export function buildConfig(minimalConfig = {}, hostname = globalThis.location?.hostname) {
  // Finding local realm: if hostname is localhost or IP, use auth.realm from local config; otherwise derive realm from hostname
  const isLocalhost =
    hostname === 'localhost' || hostname.startsWith('127.') || hostname === '0.0.0.0';
  const realm = !isLocalhost ? (hostname || '').split('.')[0] : minimalConfig.auth?.realm;

  // Build service endpoints from services.domain
  const servicesDomain = minimalConfig.services?.domain;
  const services = { ...minimalConfig.services };
  if (servicesDomain) {
    const apiBase = `https://${servicesDomain}`;
    for (const svc of SERVICES) {
      // Only set if not already defined explicitly
      if (!services[svc]?.endpoint) {
        services[svc] = { endpoint: `${apiBase}/${svc}-service/` };
      }
    }
  }

  // Build full config
  const fullConfig = {
    auth: {
      clientId: minimalConfig.auth?.clientId || 'iq-client',
      realm,
      url: minimalConfig.auth?.url,
    },
    services,
  };

  // Merge any additional settings from minimalConfig (excluding processed keys)
  const processedKeys = new Set(['auth', 'services']);
  for (const key of Object.keys(minimalConfig)) {
    if (!processedKeys.has(key)) {
      fullConfig[key] = minimalConfig[key];
    }
  }
  return fullConfig;
}

export default buildConfig;
